/**
 * Pressure3DField - Three.js 3D pressure height field for both feet.
 *
 * For each foot, the 86-point pressure sensor array is interpolated into a
 * smooth RBF surface (`buildRBFSurfaceFast` + `generateHeightGrid`). The
 * surface is rendered as a displaced plane whose vertex heights equal the
 * pressure values, and each vertex is colored by height via the default LUT.
 *
 * Both feet are placed side by side and can be rotated via OrbitControls.
 *
 * Performance optimizations (Phase 7.3):
 *   - GRID_RESOLUTION 32 (1024 vertices/foot, 2048 total — well under 5000).
 *   - Precomputed RBF inverse via `precomputeRBFInverse` — the 86 sensor
 *     coordinates are fixed, so the O(n³) matrix inversion runs ONCE (in
 *     useMemo). Per-frame solve is O(n²) via `buildRBFSurfaceFast`.
 *   - Shared `MeshStandardMaterial` via useMemo (vertex colors carry the
 *     per-frame coloring; the material itself never changes).
 *   - Persistent `PlaneGeometry` — created once on mount, updated in place
 *     via BufferAttribute writes (no per-frame geometry allocation/disposal).
 *   - On-demand rendering (`needsRender` flag) — the rAF loop advances
 *     OrbitControls damping every frame but only calls `renderer.render()`
 *     when something actually changed (new data, resize, user interaction).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { DualPressureArray } from '../../types/ski-data';
import {
  precomputeRBFInverse,
  buildRBFSurfaceFast,
  generateHeightGrid,
  type PrecomputedRBF,
} from '../../utils/rbf-interpolation';
import { getLUTForScheme, getColorFromLUT } from '../../utils/color-lut';
import { ColorSchemeId } from '../../types/enums';
import {
  PRESSURE_INSOLE_BOUNDS,
  PRESSURE_POINT_COORDINATES,
} from '../../data/pressure-coordinates';

export interface Pressure3DFieldProps {
  pressureArray: DualPressureArray | null;
  width: number;
  height: number;
  /** Optional color scheme for the height field coloring. Defaults to Default. */
  colorScheme?: ColorSchemeId;
}

const GRID_RESOLUTION = 32; // 32×32 = 1024 vertices per foot, 2048 total
const HEIGHT_SCALE = 60; // pressure (0-1) → world-space height
const RBF_SIGMA = 35; // Gaussian kernel bandwidth (matches original)
const FOOT_OFFSET_X = 90; // left at -90, right at +90

const INSOLE_BOUNDS = {
  minX: PRESSURE_INSOLE_BOUNDS.xMin,
  maxX: PRESSURE_INSOLE_BOUNDS.xMax,
  minY: PRESSURE_INSOLE_BOUNDS.yMin,
  maxY: PRESSURE_INSOLE_BOUNDS.yMax,
};

/**
 * Create a PlaneGeometry for one foot, pre-rotated to lie in the XZ plane
 * (Y = up) with a pre-allocated vertex color attribute.
 *
 * The geometry is created once and updated in place each frame — only the
 * position-Y and color BufferAttributes are rewritten.
 */
function createFootGeometry(): THREE.PlaneGeometry {
  const geo = new THREE.PlaneGeometry(
    INSOLE_BOUNDS.maxX - INSOLE_BOUNDS.minX,
    INSOLE_BOUNDS.maxY - INSOLE_BOUNDS.minY,
    GRID_RESOLUTION - 1,
    GRID_RESOLUTION - 1,
  );
  // The PlaneGeometry lies in the XY plane; rotate it so Y is up (height).
  geo.rotateX(-Math.PI / 2);
  // Pre-allocate the vertex color attribute (filled with zeros; updated in place).
  const vertexCount = geo.attributes.position.count;
  geo.setAttribute(
    'color',
    new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3),
  );
  return geo;
}

/**
 * Update a foot's BufferGeometry in place: compute RBF heights from the
 * precomputed inverse, displace vertices, and recolor via the LUT.
 *
 * Vertex ordering: PlaneGeometry vertices are row-major (row = Y outer,
 * col = X inner), while `generateHeightGrid` iterates X-outer / Y-inner.
 * We remap: vertex vi → (row = floor(vi / cols), col = vi % cols),
 * grid index = col * GRID_RESOLUTION + row.
 */
function updateFootGeometry(
  geo: THREE.PlaneGeometry,
  rbf: PrecomputedRBF,
  values: number[],
  lut: Uint8ClampedArray,
): void {
  const surface = buildRBFSurfaceFast(rbf, values);
  const grid = generateHeightGrid(surface, INSOLE_BOUNDS, GRID_RESOLUTION);

  const positions = geo.attributes.position as THREE.BufferAttribute;
  const colors = geo.attributes.color as THREE.BufferAttribute;

  for (let vi = 0; vi < positions.count; vi++) {
    const row = Math.floor(vi / GRID_RESOLUTION);
    const col = vi % GRID_RESOLUTION;
    const gridIdx = col * GRID_RESOLUTION + row;
    const h = grid[gridIdx].height;
    positions.setY(vi, h * HEIGHT_SCALE);
    const [r, g, b] = getColorFromLUT(lut, Math.min(1, Math.max(0, h)));
    colors.setXYZ(vi, r / 255, g / 255, b / 255);
  }
  positions.needsUpdate = true;
  colors.needsUpdate = true;
  geo.computeVertexNormals();
}

export default function Pressure3DField({
  pressureArray,
  width,
  height,
  colorScheme = ColorSchemeId.Default,
}: Pressure3DFieldProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [contextLost, setContextLost] = useState(false);

  const ctxRef = useRef<{
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    rafId: number;
    needsRender: boolean;
    leftMesh: THREE.Mesh;
    rightMesh: THREE.Mesh;
    leftGeo: THREE.PlaneGeometry;
    rightGeo: THREE.PlaneGeometry;
  } | null>(null);

  // Precompute RBF inverse for both feet — coordinates are fixed (from the
  // 86-point sensor layout), only values change per frame. This reduces the
  // per-frame RBF solve from O(n³) to O(n²) (~86× speedup for n=86).
  const leftRBF = useMemo<PrecomputedRBF>(() => {
    const coords = PRESSURE_POINT_COORDINATES.map((p) => ({ x: p.x, y: p.y }));
    return precomputeRBFInverse(coords, RBF_SIGMA);
  }, []);

  const rightRBF = useMemo<PrecomputedRBF>(() => {
    // Right foot: mirror Y axis (same as the original mirrorY flag).
    const coords = PRESSURE_POINT_COORDINATES.map((p) => ({ x: p.x, y: -p.y }));
    return precomputeRBFInverse(coords, RBF_SIGMA);
  }, []);

  // Shared material — vertex colors carry the per-frame coloring, so the
  // material itself never changes across frames.
  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
        metalness: 0.15,
        roughness: 0.65,
        flatShading: false,
      }),
    [],
  );

  // ---- Mount: build the scene once ----
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      setContextLost(true);
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0a0a0f, 300, 900);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 3000);
    camera.position.set(180, 140, 220);
    camera.lookAt(0, 0, 0);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.target.set(0, 0, 0);

    // Lighting.
    const ambient = new THREE.AmbientLight(0x6688aa, 0.6);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(100, 200, 120);
    scene.add(dir);

    // Persistent foot meshes — geometry is updated in place each frame
    // (no per-frame allocation/disposal).
    const leftGeo = createFootGeometry();
    const rightGeo = createFootGeometry();
    const leftMesh = new THREE.Mesh(leftGeo, material);
    const rightMesh = new THREE.Mesh(rightGeo, material);
    leftMesh.position.x = -FOOT_OFFSET_X;
    rightMesh.position.x = FOOT_OFFSET_X;
    leftMesh.visible = false;
    rightMesh.visible = false;
    scene.add(leftMesh, rightMesh);

    // Ground grid.
    const gridHelper = new THREE.GridHelper(600, 30, 0x223344, 0x111822);
    (gridHelper.material as THREE.Material).transparent = true;
    (gridHelper.material as THREE.Material).opacity = 0.25;
    gridHelper.position.y = -2;
    scene.add(gridHelper);

    const handle = {
      renderer,
      scene,
      camera,
      controls,
      rafId: 0,
      needsRender: true,
      leftMesh,
      rightMesh,
      leftGeo,
      rightGeo,
    };

    // On-demand rendering: the rAF loop advances OrbitControls damping every
    // frame but only calls renderer.render() when needsRender is true.
    const onControlsChange = () => {
      handle.needsRender = true;
    };
    controls.addEventListener('change', onControlsChange);

    const animate = () => {
      handle.rafId = requestAnimationFrame(animate);
      controls.update();
      if (handle.needsRender) {
        renderer.render(scene, camera);
        handle.needsRender = false;
      }
    };
    animate();

    ctxRef.current = handle;

    return () => {
      cancelAnimationFrame(handle.rafId);
      renderer.domElement.removeEventListener(
        'webglcontextlost',
        handleContextLost,
      );
      controls.removeEventListener('change', onControlsChange);
      controls.dispose();
      leftGeo.dispose();
      rightGeo.dispose();
      gridHelper.geometry.dispose();
      (gridHelper.material as THREE.Material).dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      ctxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dispose the memoized material on unmount.
  useEffect(() => {
    return () => material.dispose();
  }, [material]);

  // ---- Update geometry in place when pressure data or color scheme changes ----
  useEffect(() => {
    const handle = ctxRef.current;
    if (!handle) return;

    if (!pressureArray) {
      handle.leftMesh.visible = false;
      handle.rightMesh.visible = false;
      handle.needsRender = true;
      return;
    }

    const lut = getLUTForScheme(colorScheme);
    const leftValues = pressureArray.left.points.map((p) => p.value);
    const rightValues = pressureArray.right.points.map((p) => p.value);

    updateFootGeometry(handle.leftGeo, leftRBF, leftValues, lut);
    updateFootGeometry(handle.rightGeo, rightRBF, rightValues, lut);
    handle.leftMesh.visible = true;
    handle.rightMesh.visible = true;
    handle.needsRender = true;
  }, [pressureArray, colorScheme, leftRBF, rightRBF]);

  // ---- Handle resize ----
  useEffect(() => {
    const handle = ctxRef.current;
    if (!handle) return;
    handle.renderer.setSize(width, height);
    handle.camera.aspect = width / height;
    handle.camera.updateProjectionMatrix();
    handle.needsRender = true;
  }, [width, height]);

  if (contextLost) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-tertiary)',
          background: 'var(--bg-card)',
          borderRadius: 12,
          fontSize: 12,
        }}
      >
        WebGL 不可用，无法显示 3D 压力场
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, height }}>
      <div ref={mountRef} style={{ width, height }} />
      {!pressureArray && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'var(--text-tertiary)',
            fontSize: 13,
            pointerEvents: 'none',
          }}
        >
          暂无压力数据
        </div>
      )}
    </div>
  );
}
